import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { WorldMap } from "@/components/WorldMap";
import { MagicStoryCard } from "@/components/MagicStoryCard";
import { WelcomeBack } from "@/components/WelcomeBack";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, MessageCircle, Send, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";

export default function MapHome() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [meta, setMeta] = useState(null);
  const [shares, setShares] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [welcome, setWelcome] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const [m, s] = await Promise.all([api.get("/meta"), api.get("/shares")]);
    setMeta(m.data);
    setShares(s.data);
  };

  useEffect(() => {
    load();
    const raw = sessionStorage.getItem("bind_welcome");
    if (raw) {
      try {
        setWelcome(JSON.parse(raw));
      } catch (e) {}
    }
  }, []);

  const closeWelcome = () => {
    sessionStorage.removeItem("bind_welcome");
    setWelcome(null);
  };

  const openShare = async (s) => {
    const res = await api.get(`/shares/${s.id}`);
    setSelected({ ...s, ...res.data });
  };

  const toggleLike = async (id) => {
    const res = await api.post(`/shares/${id}/like`);
    setSelected((p) => (p ? { ...p, ...res.data } : p));
    setShares((prev) =>
      prev.map((x) => (x.id === id ? { ...x, like_count: res.data.like_count, liked_by_me: res.data.liked_by_me } : x))
    );
  };

  const submitComment = async (id) => {
    if (!comment.trim()) return;
    const res = await api.post(`/shares/${id}/comment`, { text: comment });
    setSelected((p) => ({ ...p, comments: [...(p.comments || []), res.data] }));
    setComment("");
    toast.success(t("toast.commented"));
  };

  // render a share in the viewer's current language (bilingual share cards)
  const loc = (s) => {
    const m = (s.magic_i18n && s.magic_i18n[lang]) || s.magic;
    const cr = {
      ...s.creature,
      name: m?.creature_name || s.creature?.name,
      description: m?.creature_description || s.creature?.description,
      trail_style: m?.trail_style || s.creature?.trail_style,
    };
    const country = (lang === "en" ? s.country_name_en : s.country_name) || s.country_name;
    return { magic: m, creature: cr, country };
  };

  return (
    <div className="space-y-6">
      {welcome && (
        <WelcomeBack stats={welcome} name={user?.name} onClose={closeWelcome} />
      )}
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-serif text-4xl font-semibold tracking-tighter sm:text-5xl">
            <span className="gold-text">{t("map.title")}</span>
          </h1>
          <p className="mt-2 text-sm text-[#A3B1C6]">
            {t("map.sub", { count: shares.length })}
          </p>
        </div>
        <Button
          onClick={() => navigate("/chat")}
          data-testid="create-entry-button"
          className="rounded-full bg-gradient-to-r from-[#D4AF37] to-[#f0d787] text-[#050814] hover:opacity-90"
        >
          <Plus className="mr-1 h-4 w-4" /> {t("map.record")}
        </Button>
      </div>

      <WorldMap
        mapImage={meta?.map_image}
        shares={shares}
        onSelect={openShare}
        selectedId={selected?.id}
      />

      <div>
        <h2 className="mb-3 font-serif text-2xl text-[#FDFBF7]">{t("map.latest")}</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shares.slice(0, 9).map((s) => {
            const L = loc(s);
            return (
            <motion.button
              key={s.id}
              whileHover={{ y: -4 }}
              onClick={() => openShare(s)}
              data-testid={`share-card-${s.id}`}
              className="text-left"
            >
              <MagicStoryCard
                magic={L.magic}
                creature={L.creature}
                region={L.country || t("story.myth")}
                mood={s.mood}
                moodEmoji={s.mood_emoji}
                compact
                footer={
                  <div className="flex items-center gap-4 text-xs text-[#A3B1C6]">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> {L.country || t("map.hidden")}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="h-3.5 w-3.5" /> {s.like_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-3.5 w-3.5" /> {s.comment_count}
                    </span>
                    <span className="ml-auto">by {s.user_name}</span>
                  </div>
                }
              />
            </motion.button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#D4AF37]/30 bg-[#050814]/95 backdrop-blur-2xl sm:max-w-2xl">
          {selected && (
            <div className="space-y-4">
              <DialogTitle className="sr-only">{selected.magic?.magic_title || "魔法故事"}</DialogTitle>
              <div className="flex items-center gap-3">
                {selected.user_picture ? (
                  <img src={selected.user_picture} alt="" className="h-10 w-10 rounded-full border border-[#D4AF37]/50" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#0A0F1D]">
                    {selected.user_name?.[0]}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium">{selected.user_name}</p>
                  <p className="text-xs text-[#A3B1C6]">{loc(selected).country || t("story.myth")}</p>
                </div>
              </div>

              <MagicStoryCard
                magic={loc(selected).magic}
                creature={loc(selected).creature}
                region={loc(selected).country}
                mood={selected.mood}
                moodEmoji={selected.mood_emoji}
              />

              <div className="flex items-center gap-4">
                <button
                  onClick={() => toggleLike(selected.id)}
                  data-testid="like-button"
                  className={`crystal flex items-center gap-1.5 border px-4 py-2 text-sm transition-colors ${
                    selected.liked_by_me
                      ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                      : "border-white/10 text-[#A3B1C6] hover:text-[#D4AF37]"
                  }`}
                >
                  <Heart className={`h-4 w-4 ${selected.liked_by_me ? "fill-[#D4AF37]" : ""}`} />
                  {selected.like_count} {t("map.likes")}
                </button>
              </div>

              <div className="space-y-3">
                <p className="text-xs tracking-[0.2em] text-[#A3B1C6]/70">{t("map.comments_label")}</p>
                {(selected.comments || []).map((c) => (
                  <div key={c.id} className="rounded-lg bg-black/30 p-3 text-sm">
                    <span className="font-medium text-[#D4AF37]">{c.user_name}</span>
                    <span className="ml-2 text-[#A3B1C6]">{c.text}</span>
                  </div>
                ))}
                {(!selected.comments || selected.comments.length === 0) && (
                  <p className="text-sm text-[#5C6A82]">{t("map.no_comments")}</p>
                )}
                <div className="flex gap-2">
                  <Input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitComment(selected.id)}
                    placeholder={t("map.comment_ph")}
                    data-testid="comment-input"
                    className="border-white/10 bg-[#0A0F1D]"
                  />
                  <Button
                    onClick={() => submitComment(selected.id)}
                    data-testid="comment-submit"
                    className="bg-[#D4AF37] text-[#050814] hover:opacity-90"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
